import { AnimatePresence, motion } from 'framer-motion';
import {
  Award, BarChart2, BookOpen, Briefcase, CheckCircle, ChevronDown,
  Cpu, GraduationCap, Lock, Monitor, Quote,
  Search, Shield, Star, TrendingUp, Users, Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import logo from '../logo/lo.png';

// ─── CONFIG — replace all REPLACE_WITH_* values before go-live ────────────────
const RAZORPAY_KEY_ID = 'rzp_live_T1999B6x9iaJEh';
const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbye2aUU3KRybjhFyMsisBKKq_IKGVd0b_7go0NxuUP4IattJFzrHJjqSJixTR6DpLI/exec';
const GA4_ID          = 'G-P0VPYJHRP1';
const META_PIXEL_ID   = 'REPLACE_WITH_YOUR_PIXEL_ID';
const MEET_LINK       = 'https://meet.google.com/mom-ekhk-owr';
const HOST_NAME       = 'HARISH UP';
const HOST_BIO        = 'Harish wears multiple hats at Vista Tech. He leads both the Full Stack and Digital Marketing verticals, guiding students through development projects and real-world ad campaigns. From building portfolio websites to optimizing SEO and running paid ads — he ensures students not only learn, but implement and analyze what they learn.';

const EVENT_START_UTC = new Date('2026-08-19T13:30:00Z');
const CAL_LINK =
  'https://calendar.google.com/calendar/render?action=TEMPLATE' +
  '&text=Data+Analytics+Masterclass+%E2%80%94+Vista+Tech' +
  '&dates=20260616T140000Z/20260616T153000Z' +
  '&details=' + encodeURIComponent('Join via Google Meet: ' + MEET_LINK) +
  '&location=' + encodeURIComponent(MEET_LINK);

declare global {
  interface Window { Razorpay: any; dataLayer: any[]; gtag?: (...a: any[]) => void; fbq?: (...a: any[]) => void; }
}

// ─── DATA ─────────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  { q: 'Is it really just ₹1?', a: 'Yes. The ₹1 is a commitment token so only serious learners attend. It covers your seat, the certificate, and downloadable class notes.' },
  { q: 'Is this a live class or a recording?', a: 'It is 100% live on August 19, 2026 from 7:00 to 8:30 PM IST over Google Meet. You can ask questions in real time.' },
  { q: 'Will I get a recording after the class?', a: 'Yes — all paid registrants receive access to the recording. Details are sent by email after the session.' },
  { q: 'Do I need to know Excel already?', a: 'Not at all. We start from absolute basics (rows, columns, formulas) and move progressively to advanced features and AI-powered dashboards.' },
  { q: 'What do I need to join?', a: 'A laptop or desktop (mobile screen is too small for Excel), a stable internet connection, and a free Claude.ai account — we show you how to set it up inside the class.' },
  { q: 'Will I get a certificate and class notes?', a: 'Yes — both are included free with your ₹1 ticket. Certificate and notes are sent automatically after the session.' },
  { q: 'How do I join the live class?', a: 'After payment, the Google Meet link is shown on screen immediately and emailed to you. Save the calendar invite so you do not forget!' },
  { q: 'What if my payment fails?', a: 'Your card is not charged on a failed payment. Simply retry — the form stays open. Write to vistatech.in@gmail.com if you need help.' },
];

const CURRICULUM = [
  {
    Icon: BookOpen,
    module: '01',
    title: 'Excel from Zero to Confident',
    desc: 'Master rows, columns, 30+ essential formulas, data cleaning, and conditional formatting. Build a solid foundation even if you have never opened Excel before.',
    tag: 'Foundation',
    color: '#4e3a69',
    bg: 'rgba(78,58,105,0.08)',
  },
  {
    Icon: BarChart2,
    module: '02',
    title: 'Advanced Excel & Dashboards',
    desc: 'Unlock pivot tables, VLOOKUP / XLOOKUP, dynamic charts, and interactive dashboards that impress managers and interviewers.',
    tag: 'Advanced',
    color: '#2b2240',
    bg: 'rgba(43,34,64,0.08)',
  },
  {
    Icon: Cpu,
    module: '03',
    title: 'Build an AI-Powered Dashboard',
    desc: 'Feed real business data into Claude AI and build a smart, auto-updating analytics dashboard — no heavy coding required.',
    tag: 'AI + Claude',
    color: '#FF6535',
    bg: 'rgba(255,101,53,0.08)',
  },
];

const AUDIENCE = [
  { Icon: GraduationCap, label: 'Students', desc: 'Add a high-value data analytics skill to your resume before you even graduate.', iconBg: '#4e3a69', textColor: '#fff' },
  { Icon: Briefcase,     label: 'Working Professionals', desc: 'Automate reports, impress your manager, and work smarter with data.', iconBg: '#2b2240', textColor: '#fff' },
  { Icon: Search,        label: 'Job Seekers', desc: 'Data analytics is one of the most in-demand skills — stand out to recruiters.', iconBg: '#FF6535', textColor: '#fff' },
  { Icon: TrendingUp,    label: 'Entrepreneurs', desc: 'Understand your business numbers and make decisions backed by data, not gut.', iconBg: '#F0B429', textColor: '#2b2240' },
];

const TESTIMONIALS = [
  { initials: 'PS', name: 'Priya S.', role: 'Final-year BCA Student', text: 'I had zero Excel knowledge. After the session I built my own dashboard for a college project. The Claude AI part was mind-blowing.', avatarBg: '#4e3a69' },
  { initials: 'RM', name: 'Rahul M.', role: 'Sales Executive', text: 'The pivot table section alone saves me 3 hours every week on my reports. Cannot believe I got all this for ₹1.', avatarBg: '#2b2240' },
  { initials: 'AK', name: 'Anjali K.', role: 'Fresher — Placed at TCS', text: 'I mentioned the AI dashboard in my interview. The interviewer was genuinely impressed. Got placed 2 weeks later!', avatarBg: '#FF6535' },
];

const BONUSES = [
  { Icon: Award,    title: 'Certificate of Completion', desc: 'Shareable on LinkedIn and your resume — verifiable proof of your data analytics skills.', iconColor: '#F0B429', borderColor: '#fde68a' },
  { Icon: BookOpen, title: 'Downloadable Class Notes',   desc: 'A structured PDF reference so you can revisit every formula, pivot table, and AI technique anytime.', iconColor: '#4e3a69', borderColor: '#c4b5fd' },
];

// ─── ANALYTICS ────────────────────────────────────────────────────────────────
const gtagEvent = (name: string, params?: Record<string, any>) => { if (typeof window.gtag === 'function') window.gtag('event', name, params); };
const pixelTrack = (name: string, params?: Record<string, any>) => { if (typeof window.fbq === 'function') window.fbq('track', name, params); };

// ─── COUNTDOWN ────────────────────────────────────────────────────────────────
function useCountdown(target: Date) {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
    return { d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000), expired: false };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    if (t.expired) return;
    const id = setInterval(() => setT(calc), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return t;
}

// ─── SEO ──────────────────────────────────────────────────────────────────────
function useSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Data Analytics Masterclass — Excel + AI Dashboards | Vista Tech Solutions';

    const upsert = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLElement | null;
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };

    upsert('meta[name="description"]', 'content', 'Live 90-min Data Analytics Masterclass — August 19 2026, 7:00–8:30 PM IST. Learn Excel basics to advanced + build an AI-powered dashboard in Claude. Just ₹1. Limited seats.');
    upsert('meta[name="robots"]', 'content', 'index, follow');
    upsert('meta[property="og:title"]', 'content', 'Data Analytics Masterclass — Excel + AI Dashboards | Vista Tech');
    upsert('meta[property="og:description"]', 'content', 'Master Excel & build AI dashboards in one live 90-min class. August 19 · 7:00–8:30 PM IST · Just ₹1. Seats are limited — register now!');
    upsert('meta[property="og:type"]', 'content', 'website');
    upsert('meta[property="og:url"]', 'content', 'https://vistatech.org/data-analytics-masterclass');
    upsert('meta[property="og:image"]', 'content', 'https://vistatech.org/assets/masterclass-og.jpg');
    upsert('meta[name="twitter:card"]', 'content', 'summary_large_image');
    upsert('meta[name="twitter:title"]', 'content', 'Data Analytics Masterclass — Excel + AI Dashboards | Vista Tech');
    upsert('meta[name="twitter:description"]', 'content', 'Live · August 19 · Excel basics to advanced + AI dashboard in Claude · ₹1 only.');
    upsert('meta[name="twitter:image"]', 'content', 'https://vistatech.org/assets/masterclass-og.jpg');

    let canon = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canon) { canon = document.createElement('link'); (canon as HTMLLinkElement).rel = 'canonical'; document.head.appendChild(canon); }
    canon.href = 'https://vistatech.org/data-analytics-masterclass';

    const ld = document.createElement('script');
    ld.type = 'application/ld+json'; ld.id = 'mc-ld';
    ld.textContent = JSON.stringify([
      { '@context': 'https://schema.org', '@type': 'EducationEvent', name: 'Data Analytics Masterclass — Excel + AI Dashboards', description: 'A live 90-minute online masterclass covering Excel from basics to advanced, and building an AI-powered dashboard using Claude. No prior experience needed.', startDate: '2026-08-19T19:00:00+05:30', endDate: '2026-08-19T20:30:00+05:30', eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode', eventStatus: 'https://schema.org/EventScheduled', location: { '@type': 'VirtualLocation', url: MEET_LINK }, organizer: { '@type': 'Organization', name: 'Vista Tech Solutions', url: 'https://vistatech.org' }, offers: { '@type': 'Offer', price: '1', priceCurrency: 'INR', availability: 'https://schema.org/InStock', validFrom: '2026-08-13T00:00:00+05:30', url: 'https://vistatech.org/data-analytics-masterclass' }, image: 'https://vistatech.org/assets/masterclass-og.jpg' },
      { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQ_ITEMS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [ { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vistatech.org' }, { '@type': 'ListItem', position: 2, name: 'Courses', item: 'https://vistatech.org/courses' }, { '@type': 'ListItem', position: 3, name: 'Data Analytics Masterclass', item: 'https://vistatech.org/data-analytics-masterclass' } ] },
    ]);
    document.head.appendChild(ld);
    return () => { document.title = prevTitle; document.getElementById('mc-ld')?.remove(); };
  }, []);
}

// ─── VALIDATION ───────────────────────────────────────────────────────────────
function validateForm(f: { name: string; email: string; phone: string }) {
  const e: Record<string, string> = {};
  if (!f.name.trim()) e.name = 'Please enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = 'Please enter a valid email address.';
  if (!/^[6-9]\d{9}$/.test(f.phone.replace(/\s/g, ''))) e.phone = 'Enter a valid 10-digit Indian mobile number.';
  return e;
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
const CountdownTile = ({ v, l }: { v: number; l: string }) => (
  <div style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, padding: '12px 16px', minWidth: 64, textAlign: 'center' }}>
    <p style={{ fontSize: 28, fontWeight: 800, color: '#F0B429', lineHeight: 1, margin: 0 }}>{String(v).padStart(2, '0')}</p>
    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{l}</p>
  </div>
);

const StarRow = ({ size = 14 }: { size?: number }) => (
  <span style={{ display: 'inline-flex', gap: 2 }}>
    {[1,2,3,4,5].map(i => <Star key={i} size={size} style={{ color: '#F0B429', fill: '#F0B429' }} aria-hidden="true" />)}
  </span>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function DataAnalyticsMasterclass() {
  const [form, setForm]                 = useState({ name: '', email: '', phone: '', profession: '' });
  const [errors, setErrors]             = useState<Record<string, string>>({});
  const [step, setStep]                 = useState<'form' | 'loading' | 'success' | 'failed'>('form');
  const [paymentError, setPaymentError] = useState('');
  const [openFaq, setOpenFaq]           = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const utmRef  = useRef<Record<string, string>>({});
  const cd      = useCountdown(EVENT_START_UTC);

  useSEO();

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    ['utm_source','utm_medium','utm_campaign','utm_content'].forEach(k => { const v = p.get(k); if (v) utmRef.current[k] = v; });
  }, []);

  useEffect(() => {
    if (document.querySelector('script[src*="checkout.razorpay.com"]')) return;
    const s = document.createElement('script'); s.src = 'https://checkout.razorpay.com/v1/checkout.js'; s.async = true; document.body.appendChild(s);
    return () => { s.remove(); };
  }, []);

  useEffect(() => {
    if (!GA4_ID || GA4_ID.includes('REPLACE')) return;
    const s = document.createElement('script'); s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`; s.async = true; document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments as any); };
    window.gtag('js', new Date()); window.gtag('config', GA4_ID);
  }, []);

  useEffect(() => {
    if (!META_PIXEL_ID || META_PIXEL_ID.includes('REPLACE') || window.fbq) return;
    const n: any = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    window.fbq = n; n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    const t = document.createElement('script'); t.async = true; t.src = 'https://connect.facebook.net/en_US/fbevents.js'; document.head.appendChild(t);
    n('init', META_PIXEL_ID); n('track', 'PageView');
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleChange = (k: string, v: string) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => { const n = { ...p }; delete n[k]; return n; });
  };

  const handlePayment = async () => {
    const errs = validateForm(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep('loading'); setPaymentError('');
    gtagEvent('begin_checkout', { currency: 'INR', value: 1 });
    pixelTrack('InitiateCheckout', { currency: 'INR', value: 1 });
    try {
      const orderRes  = await fetch(APPS_SCRIPT_URL, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ action: 'createOrder', name: form.name, email: form.email, phone: form.phone }) });
      const orderData = await orderRes.json();
      if (orderData.status !== 'success') throw new Error(orderData.message || 'Could not create payment order.');
      const rzp = new window.Razorpay({
        key: RAZORPAY_KEY_ID, amount: 100, currency: 'INR',
        name: 'Vista Tech Solutions', description: 'Data Analytics Masterclass — August 19, 2026',
        order_id: orderData.orderId, prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#2b2240' },
        modal: { ondismiss: () => { setStep('form'); setPaymentError('Payment was cancelled. Your details are saved — click below to try again.'); } },
        handler: async (r: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
          try {
            const vRes  = await fetch(APPS_SCRIPT_URL, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ action: 'verifyAndRegister', razorpay_payment_id: r.razorpay_payment_id, razorpay_order_id: r.razorpay_order_id, razorpay_signature: r.razorpay_signature, name: form.name, email: form.email, phone: form.phone, profession: form.profession, ...utmRef.current }) });
            const vData = await vRes.json();
            if (vData.status !== 'success') throw new Error(vData.message || 'Verification failed.');
            gtagEvent('purchase', { transaction_id: r.razorpay_payment_id, value: 1, currency: 'INR' });
            pixelTrack('Purchase', { currency: 'INR', value: 1 });
            setStep('success');
          } catch (err: any) { setStep('failed'); setPaymentError(err.message || 'Verification failed. Contact vistatech.in@gmail.com.'); }
        },
      });
      rzp.open();
    } catch (err: any) { setStep('form'); setPaymentError(err.message || 'Something went wrong. Please try again.'); }
  };

  // ── styles ──────────────────────────────────────────────────────────────────
  const inputBase: React.CSSProperties = { width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '13px 16px', fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 0.2s' };
  const inputError: React.CSSProperties = { ...inputBase, borderColor: '#f87171', background: '#fef2f2' };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: "'Poppins', sans-serif" }}>

      {/* ── STICKY HEADER ──────────────────────────────────────────────────── */}
      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #f0f0f0', boxShadow: '0 1px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 20px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" aria-label="Vista Tech Solutions home">
            <img src={logo} alt="Vista Tech Solutions" style={{ height: 34, width: 'auto' }} width={130} height={34} />
          </a>
          <button onClick={scrollToForm} style={{ background: '#F0B429', color: '#2b2240', border: 'none', borderRadius: 50, padding: '10px 24px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.01em' }}>
            Register Now
          </button>
        </div>
      </header>

      <main style={{ paddingTop: 60 }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <section style={{ background: 'linear-gradient(135deg, #1a1030 0%, #2b2240 45%, #3d2d5c 100%)', color: '#fff', padding: '72px 20px 64px', position: 'relative', overflow: 'hidden' }} aria-labelledby="hero-heading">
          {/* decorative orbs */}
          <div aria-hidden="true" style={{ position: 'absolute', top: -120, right: -120, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,180,41,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div aria-hidden="true" style={{ position: 'absolute', bottom: -80, left: -80, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,101,53,0.10) 0%, transparent 70%)', pointerEvents: 'none' }} />

          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
            {/* Eyebrow */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.3)', borderRadius: 50, padding: '6px 16px', marginBottom: 24 }}>
              <Zap size={13} style={{ color: '#F0B429' }} aria-hidden="true" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#F0B429', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Live Online Event · August 19, 2026</span>
            </div>

            <h1 id="hero-heading" style={{ fontSize: 'clamp(26px, 5vw, 42px)', fontWeight: 800, lineHeight: 1.2, margin: '0 0 20px', letterSpacing: '-0.01em' }}>
              Master Data Analytics with<br />
              <span style={{ background: 'linear-gradient(90deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Excel + AI Dashboards</span><br />
              — in One Live 90-Minute Class.
            </h1>

            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.72)', marginBottom: 32, lineHeight: 1.6 }}>
              Go from spreadsheets to AI-powered dashboards. No prior experience needed.
            </p>

            {/* Logistics bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 20px', marginBottom: 36 }}>
              {[['📅', 'August 19, 2026'], ['🕢', '7:00–8:30 PM IST'], ['⏱', '90 Minutes'], ['💻', 'Live & Online']].map(([icon, text]) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 50, padding: '6px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
                  <span aria-hidden="true">{icon}</span> {text}
                </div>
              ))}
            </div>

            {/* Price card */}
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 24, padding: '20px 40px', marginBottom: 32, backdropFilter: 'blur(8px)' }}>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textDecoration: 'line-through', margin: '0 0 4px' }}>Worth ₹2,000</p>
              <p style={{ fontSize: 52, fontWeight: 900, color: '#F0B429', lineHeight: 1, margin: '0 0 6px', letterSpacing: '-0.02em' }}>₹1</p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, margin: 0 }}>One rupee. No hidden charges.</p>
            </div>

            <div style={{ marginBottom: 28 }}>
              <button onClick={scrollToForm} style={{ background: 'linear-gradient(135deg, #F0B429, #e6a820)', color: '#2b2240', border: 'none', borderRadius: 50, padding: '16px 40px', fontWeight: 800, fontSize: 16, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 30px rgba(240,180,41,0.4)', letterSpacing: '0.01em', transition: 'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 12px 40px rgba(240,180,41,0.5)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 30px rgba(240,180,41,0.4)'; }}
              >
                Register for ₹1 →
              </button>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 10 }}>
                <Lock size={11} style={{ display: 'inline', marginRight: 4 }} aria-hidden="true" />
                Secure payment via Razorpay · Seat confirmed only after payment
              </p>
            </div>

            {/* Countdown */}
            {!cd.expired && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 28 }} aria-label="Time remaining until event">
                <CountdownTile v={cd.d} l="Days" />
                <CountdownTile v={cd.h} l="Hours" />
                <CountdownTile v={cd.m} l="Mins" />
                <CountdownTile v={cd.s} l="Secs" />
              </div>
            )}

            {/* Social proof */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
              <StarRow size={14} />
              <span>Trusted by <strong style={{ color: 'rgba(255,255,255,0.8)' }}>5,000+</strong> learners across India</span>
            </div>
          </div>
        </section>

        {/* ── FOR WHO? ──────────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: '#f8f7fc' }} aria-labelledby="audience-heading">
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            <p style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Who Is This For</p>
            <h2 id="audience-heading" style={{ textAlign: 'center', fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#2b2240', margin: '0 0 48px', letterSpacing: '-0.01em' }}>
              Built For Anyone Ready to Level Up With Data
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
              {AUDIENCE.map(({ Icon, label, desc, iconBg, textColor }) => (
                <motion.div
                  key={label}
                  whileHover={{ y: -4, boxShadow: '0 20px 50px rgba(43,34,64,0.12)' }}
                  style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', boxShadow: '0 2px 16px rgba(43,34,64,0.06)', border: '1px solid rgba(43,34,64,0.06)', cursor: 'default' }}
                >
                  <div style={{ width: 52, height: 52, borderRadius: 16, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: `0 4px 14px ${iconBg}50` }}>
                    <Icon size={24} style={{ color: textColor }} aria-hidden="true" />
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#2b2240', margin: '0 0 8px' }}>{label}</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CURRICULUM ────────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: '#fff' }} aria-labelledby="curriculum-heading">
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <p style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Curriculum</p>
            <h2 id="curriculum-heading" style={{ textAlign: 'center', fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#2b2240', margin: '0 0 12px', letterSpacing: '-0.01em' }}>
              What You Will Learn
            </h2>
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, marginBottom: 44 }}>
              Three focused modules. One 90-minute session. Skills you can apply on Monday.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {CURRICULUM.map((c, i) => (
                <motion.div
                  key={i}
                  whileHover={{ x: 4 }}
                  style={{ display: 'flex', gap: 20, padding: '24px 28px', borderRadius: 20, border: `1.5px solid ${c.color}22`, background: c.bg, alignItems: 'flex-start' }}
                >
                  {/* Module number + icon */}
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${c.color}40` }}>
                      <c.Icon size={26} style={{ color: '#fff' }} aria-hidden="true" />
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: c.color, background: `${c.color}18`, padding: '3px 10px', borderRadius: 50, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{c.tag}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>MODULE {c.module}</span>
                    </div>
                    <h3 style={{ fontSize: 17, fontWeight: 700, color: '#2b2240', margin: '0 0 8px' }}>{c.title}</h3>
                    <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65, margin: 0 }}>{c.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BONUSES ────────────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: 'linear-gradient(135deg, #f8f7fc, #fefce8)' }} aria-labelledby="bonuses-heading">
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Included Free</p>
            <h2 id="bonuses-heading" style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#2b2240', margin: '0 0 10px', letterSpacing: '-0.01em' }}>
              What You Get With Your ₹1 Ticket
            </h2>
            <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 44 }}>Certificate + class notes — both delivered automatically after the session</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, maxWidth: 640, margin: '0 auto' }}>
              {BONUSES.map(b => (
                <div key={b.title} style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', border: `1.5px solid ${b.borderColor}`, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', textAlign: 'left' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: `${b.iconColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <b.Icon size={24} style={{ color: b.iconColor }} aria-hidden="true" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <CheckCircle size={16} style={{ color: '#22c55e', flexShrink: 0 }} aria-hidden="true" />
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#2b2240', margin: 0 }}>{b.title}</h3>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── INSTRUCTOR ────────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: '#fff' }} aria-labelledby="instructor-heading">
          <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Your Instructor</p>
            <h2 id="instructor-heading" style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#2b2240', margin: '0 0 40px', letterSpacing: '-0.01em' }}>
              Learn From an Expert
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
              {/* Avatar — replace inner content with <img> when photo is ready */}
              <div style={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, #4e3a69, #2b2240)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '4px solid rgba(240,180,41,0.4)', boxShadow: '0 8px 30px rgba(43,34,64,0.25)' }}>
                <Users size={44} style={{ color: 'rgba(255,255,255,0.6)' }} aria-hidden="true" />
                {/* To add photo: <img src={hostPhotoPath} alt={HOST_NAME} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} /> */}
              </div>
              <div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: '#2b2240', margin: '0 0 4px' }}>{HOST_NAME}</h3>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#4e3a69', margin: '0 0 14px' }}>Data Analytics Trainer · Vista Tech Solutions</p>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, maxWidth: 480, margin: '0 auto 20px' }}>{HOST_BIO}</p>
                <img src={logo} alt="Vista Tech Solutions" style={{ height: 28, width: 'auto', opacity: 0.55 }} width={100} height={28} />
              </div>
            </div>
          </div>
        </section>

        {/* ── SOCIAL PROOF ──────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: '#f8f7fc' }} aria-labelledby="testimonials-heading">
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            <p style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Learner Stories</p>
            <h2 id="testimonials-heading" style={{ textAlign: 'center', fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#2b2240', margin: '0 0 12px', letterSpacing: '-0.01em' }}>
              What Our Learners Say
            </h2>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 44 }}>
              <StarRow size={18} />
              <span style={{ fontSize: 14, color: '#6b7280' }}>4.9 / 5 from 200+ reviews</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
              {TESTIMONIALS.map(t => (
                <figure key={t.name} style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', margin: 0, boxShadow: '0 2px 16px rgba(43,34,64,0.07)', border: '1px solid rgba(43,34,64,0.06)' }}>
                  <Quote size={28} style={{ color: '#e5e7eb', marginBottom: 12 }} aria-hidden="true" />
                  <blockquote style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, margin: '0 0 20px', fontStyle: 'italic' }}>
                    "{t.text}"
                  </blockquote>
                  <figcaption style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: t.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>{t.initials}</span>
                    </div>
                    <div>
                      <p style={{ fontWeight: 700, color: '#2b2240', fontSize: 14, margin: '0 0 2px' }}>{t.name}</p>
                      <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── REGISTRATION FORM ─────────────────────────────────────────────── */}
        <section
          ref={formRef}
          id="register"
          style={{ padding: '72px 20px', background: 'linear-gradient(135deg, #1a1030 0%, #2b2240 100%)', position: 'relative', overflow: 'hidden' }}
          aria-labelledby="register-heading"
        >
          <div aria-hidden="true" style={{ position: 'absolute', top: -100, right: -100, width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,180,41,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ maxWidth: 520, margin: '0 auto', position: 'relative' }}>
            <AnimatePresence mode="wait">

              {/* SUCCESS */}
              {step === 'success' && (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ background: '#fff', borderRadius: 28, padding: '40px 32px', textAlign: 'center' }} role="status" aria-live="polite">
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <CheckCircle size={40} style={{ color: '#22c55e' }} aria-hidden="true" />
                  </div>
                  <h2 style={{ fontSize: 26, fontWeight: 800, color: '#2b2240', margin: '0 0 10px' }}>You're In! 🎉</h2>
                  <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
                    Your seat is confirmed. The meeting link has been emailed to <strong style={{ color: '#2b2240' }}>{form.email}</strong>.
                  </p>
                  <div style={{ background: '#f8f7fc', border: '1px solid #e9e5f7', borderRadius: 16, padding: '18px 20px', marginBottom: 24, textAlign: 'left' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>Your Meeting Link</p>
                    <a href={MEET_LINK} target="_blank" rel="noopener noreferrer" style={{ color: '#4e3a69', fontWeight: 700, fontSize: 14, wordBreak: 'break-all' }}>{MEET_LINK}</a>
                    <p style={{ fontSize: 12, color: '#9ca3af', margin: '8px 0 0' }}>📅 August 19, 2026 · 🕢 7:00–8:30 PM IST · ⏱ 90 min</p>
                  </div>
                  <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" style={{ display: 'block', background: '#2b2240', color: '#fff', borderRadius: 50, padding: '14px 24px', textDecoration: 'none', fontWeight: 700, fontSize: 14, marginBottom: 14 }}>
                    📅 Add to Google Calendar
                  </a>
                  <p style={{ fontSize: 12, color: '#9ca3af' }}>Keep ready: laptop · stable internet · free Claude.ai account</p>
                </motion.div>
              )}

              {/* FORM */}
              {step !== 'success' && (
                <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ background: '#fff', borderRadius: 28, padding: '40px 32px', boxShadow: '0 40px 80px rgba(0,0,0,0.35)' }}>
                  <h2 id="register-heading" style={{ fontSize: 22, fontWeight: 800, color: '#2b2240', textAlign: 'center', margin: '0 0 6px' }}>
                    Secure Your Seat for ₹1
                  </h2>
                  <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginBottom: 28 }}>
                    August 19, 2026 · 7:00–8:30 PM IST · Limited seats available
                  </p>

                  {/* Trust badges */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
                    {[
                      { Icon: Shield, text: 'Secure Payment' },
                      { Icon: Lock,   text: 'Razorpay Protected' },
                    ].map(b => (
                      <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6b7280' }}>
                        <b.Icon size={13} style={{ color: '#22c55e' }} aria-hidden="true" />
                        {b.text}
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Name */}
                    <div>
                      <label htmlFor="mc-name" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Full Name <span style={{ color: '#ef4444' }} aria-hidden="true">*</span>
                      </label>
                      <input id="mc-name" type="text" autoComplete="name" placeholder="e.g. Priya Sharma" value={form.name} onChange={e => handleChange('name', e.target.value)} aria-required="true" aria-invalid={!!errors.name} style={errors.name ? inputError : inputBase} />
                      {errors.name && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }} role="alert">{errors.name}</p>}
                    </div>
                    {/* Email */}
                    <div>
                      <label htmlFor="mc-email" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Email Address <span style={{ color: '#ef4444' }} aria-hidden="true">*</span>
                      </label>
                      <input id="mc-email" type="email" autoComplete="email" placeholder="e.g. priya@gmail.com" value={form.email} onChange={e => handleChange('email', e.target.value)} aria-required="true" aria-invalid={!!errors.email} style={errors.email ? inputError : inputBase} />
                      <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Meeting link sent here instantly after payment</p>
                      {errors.email && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }} role="alert">{errors.email}</p>}
                    </div>
                    {/* Phone */}
                    <div>
                      <label htmlFor="mc-phone" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        WhatsApp / Phone <span style={{ color: '#ef4444' }} aria-hidden="true">*</span>
                      </label>
                      <input id="mc-phone" type="tel" autoComplete="tel" placeholder="10-digit mobile number" value={form.phone} onChange={e => handleChange('phone', e.target.value.replace(/\D/g,'').slice(0,10))} aria-required="true" aria-invalid={!!errors.phone} style={errors.phone ? inputError : inputBase} />
                      {errors.phone && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }} role="alert">{errors.phone}</p>}
                    </div>
                    {/* Profession */}
                    <div>
                      <label htmlFor="mc-profession" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Your Current Status <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                      </label>
                      <select id="mc-profession" value={form.profession} onChange={e => handleChange('profession', e.target.value)} style={{ ...inputBase, background: '#fff', cursor: 'pointer' }}>
                        <option value="">— Select —</option>
                        <option value="student">Student</option>
                        <option value="working">Working Professional</option>
                        <option value="job-seeker">Job Seeker</option>
                        <option value="entrepreneur">Entrepreneur / Business Owner</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {paymentError && (
                    <div style={{ marginTop: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#dc2626' }} role="alert">
                      {paymentError}
                    </div>
                  )}

                  <button
                    onClick={handlePayment}
                    disabled={step === 'loading'}
                    style={{ width: '100%', marginTop: 24, background: step === 'loading' ? '#e5e7eb' : 'linear-gradient(135deg, #F0B429, #e6a820)', color: step === 'loading' ? '#9ca3af' : '#2b2240', border: 'none', borderRadius: 50, padding: '17px 24px', fontWeight: 800, fontSize: 16, cursor: step === 'loading' ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: step === 'loading' ? 'none' : '0 6px 24px rgba(240,180,41,0.35)', letterSpacing: '0.01em', transition: 'all 0.2s' }}
                  >
                    {step === 'loading' ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <svg style={{ animation: 'spin 1s linear infinite', width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                          <circle cx="12" cy="12" r="10" stroke="#9ca3af" strokeWidth="3" />
                          <path d="M4 12a8 8 0 018-8" stroke="#2b2240" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                        Opening Secure Payment...
                      </span>
                    ) : 'Pay ₹1 & Confirm My Seat'}
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 12, color: '#9ca3af', fontSize: 12 }}>
                    <Monitor size={12} aria-hidden="true" />
                    <span>Secure checkout · Meeting link delivered instantly</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: '#fff' }} aria-labelledby="faq-heading">
          <div style={{ maxWidth: 660, margin: '0 auto' }}>
            <p style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4e3a69', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Got Questions?</p>
            <h2 id="faq-heading" style={{ textAlign: 'center', fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#2b2240', margin: '0 0 44px', letterSpacing: '-0.01em' }}>
              Frequently Asked Questions
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FAQ_ITEMS.map((f, i) => (
                <div key={i} style={{ border: '1.5px solid', borderColor: openFaq === i ? '#4e3a69' : '#f3f4f6', borderRadius: 16, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', background: openFaq === i ? '#f8f7fc' : 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', color: '#2b2240', fontSize: 14, fontWeight: 600, gap: 12 }}
                  >
                    <span>{f.q}</span>
                    <ChevronDown size={18} style={{ color: '#4e3a69', flexShrink: 0, transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s' }} aria-hidden="true" />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
                        <p style={{ padding: '0 22px 18px', fontSize: 14, color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ─────────────────────────────────────────────────────── */}
        <section style={{ padding: '72px 20px', background: 'linear-gradient(135deg, #1a1030 0%, #2b2240 100%)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div aria-hidden="true" style={{ position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)', width: 500, height: 300, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(240,180,41,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ maxWidth: 560, margin: '0 auto', position: 'relative' }}>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 800, color: '#fff', margin: '0 0 12px', letterSpacing: '-0.01em' }}>
              Don't Miss This
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 32 }}>
              August 19, 2026 · 7:00–8:30 PM IST · Only ₹1 · Certificate + Notes included
            </p>
            {!cd.expired && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 32 }}>
                <CountdownTile v={cd.d} l="Days" />
                <CountdownTile v={cd.h} l="Hours" />
                <CountdownTile v={cd.m} l="Mins" />
                <CountdownTile v={cd.s} l="Secs" />
              </div>
            )}
            <button onClick={scrollToForm} style={{ background: 'linear-gradient(135deg, #F0B429, #e6a820)', color: '#2b2240', border: 'none', borderRadius: 50, padding: '16px 44px', fontWeight: 800, fontSize: 16, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 30px rgba(240,180,41,0.4)', letterSpacing: '0.01em' }}>
              Register for ₹1 →
            </button>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginTop: 12 }}>Seats are limited · Secure payment via Razorpay</p>
          </div>
        </section>
      </main>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer style={{ background: '#0d0b14', padding: '28px 20px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, margin: 0 }}>© {new Date().getFullYear()} Vista Tech Solutions. All rights reserved.</p>
          <nav aria-label="Legal links">
            <div style={{ display: 'flex', gap: 20 }}>
              {[['Terms & Conditions', '/Terms-and-Conditions'], ['Contact', '/contact']].map(([label, href]) => (
                <a key={href} href={href} style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, textDecoration: 'none' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.8)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
                >{label}</a>
              ))}
            </div>
          </nav>
        </div>
      </footer>

      {/* ── STICKY MOBILE CTA ─────────────────────────────────────────────── */}
      <div
        className="sm:hidden"
        style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#fff', borderTop: '1px solid #f0f0f0', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 -8px 30px rgba(0,0,0,0.12)' }}
        aria-hidden={step === 'success'}
      >
        <div>
          <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 2px' }}>Data Analytics Masterclass</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: '#2b2240', lineHeight: 1 }}>₹1</span>
            <span style={{ fontSize: 12, color: '#d1d5db', textDecoration: 'line-through' }}>₹2,000</span>
          </div>
        </div>
        <button onClick={scrollToForm} style={{ background: 'linear-gradient(135deg, #F0B429, #e6a820)', color: '#2b2240', border: 'none', borderRadius: 50, padding: '11px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(240,180,41,0.35)' }}>
          Register →
        </button>
      </div>

    </div>
  );
}
